"use client";

import { createSolanaClient } from "@metamask/connect-solana";
import { SOLANA_MAINNET_CHAIN } from "@solana/wallet-standard-chains";
import {
  SolanaSignAndSendTransaction,
  SolanaSignTransaction,
} from "@solana/wallet-standard-features";
import { StandardConnect, StandardDisconnect } from "@wallet-standard/features";
import { SOLANA_RPC_URL } from "./rpc";

const METAMASK_RDNS_PREFIX = "io.metamask";
const EIP6963_DETECT_MS = 300;

/** Wallets that spoof `window.ethereum.isMetaMask` for compatibility. */
const METAMASK_IMPERSONATORS = [
  "isBraveWallet",
  "isCoinbaseWallet",
  "isRabby",
  "isPhantom",
  "isTrust",
  "isTrustWallet",
  "isOkxWallet",
  "isBitKeep",
  "isTokenPocket",
];

let solanaClientPromise = null;
let activeWallet = null;
let eip6963Detected = null;

function isLikelyMetaMaskProvider(provider) {
  if (!provider?.isMetaMask) return false;
  return !METAMASK_IMPERSONATORS.some((flag) => provider[flag]);
}

/**
 * Sync check — filters out wallets that impersonate MetaMask via `isMetaMask`.
 */
export function isMetaMaskInstalledSync() {
  if (typeof window === "undefined") return false;

  const providers = window.ethereum?.providers;
  if (Array.isArray(providers)) {
    return providers.some(isLikelyMetaMaskProvider);
  }

  return isLikelyMetaMaskProvider(window.ethereum);
}

/**
 * EIP-6963 provider discovery — the reliable way to detect the MetaMask extension.
 */
export async function detectMetaMaskInstalled() {
  if (typeof window === "undefined") return false;
  if (eip6963Detected !== null) return eip6963Detected;

  const syncResult = isMetaMaskInstalledSync();
  if (!syncResult) {
    eip6963Detected = false;
    return false;
  }

  const announced = [];
  const handler = (event) => {
    if (event.detail?.info?.rdns) {
      announced.push(event.detail);
    }
  };

  window.addEventListener("eip6963:announceProvider", handler);
  window.dispatchEvent(new Event("eip6963:requestProvider"));

  await new Promise((resolve) => {
    setTimeout(resolve, EIP6963_DETECT_MS);
  });

  window.removeEventListener("eip6963:announceProvider", handler);

  const hasMetaMaskRdns = announced.some((entry) =>
    entry.info?.rdns?.startsWith(METAMASK_RDNS_PREFIX)
  );

  // When providers announce via EIP-6963, trust rdns over `isMetaMask` flags.
  if (announced.length > 0) {
    eip6963Detected = hasMetaMaskRdns;
    return eip6963Detected;
  }

  eip6963Detected = syncResult;
  return eip6963Detected;
}

/** Reset cached EIP-6963 result (e.g. after extension install). */
export function resetMetaMaskDetectionCache() {
  eip6963Detected = null;
}

export async function initMetaMaskSolanaClient() {
  if (typeof window === "undefined") return null;

  const installed = await detectMetaMaskInstalled();
  if (!installed) return null;

  if (!solanaClientPromise) {
    solanaClientPromise = createSolanaClient({
      dapp: {
        name: "IDX AutoBot",
        url: window.location.origin,
      },
      api: {
        supportedNetworks: {
          mainnet: SOLANA_RPC_URL || "https://api.mainnet-beta.solana.com",
        },
      },
    });
  }

  return solanaClientPromise;
}

export async function connectMetaMaskSolanaDirect() {
  const installed = await detectMetaMaskInstalled();
  if (!installed) {
    throw new Error(
      "MetaMask is not installed. Please install the MetaMask browser extension and try again."
    );
  }

  const client = await initMetaMaskSolanaClient();
  if (!client) {
    throw new Error(
      "MetaMask is not installed. Please install the MetaMask browser extension and try again."
    );
  }

  const wallet = client.getWallet();
  const { accounts } = await wallet.features[StandardConnect].connect();
  const address = accounts?.[0]?.address;

  if (!address) {
    throw new Error(
      "MetaMask connected but no Solana account was returned. Unlock MetaMask and try again."
    );
  }

  activeWallet = wallet;
  return address;
}

export async function disconnectMetaMaskSolanaDirect() {
  try {
    if (activeWallet?.features?.[StandardDisconnect]) {
      await activeWallet.features[StandardDisconnect].disconnect();
    }
  } catch {
    // MetaMask may not support a clean disconnect.
  } finally {
    activeWallet = null;
  }

  if (solanaClientPromise) {
    try {
      const client = await solanaClientPromise;
      await client.disconnect();
    } catch {
      // Ignore disconnect errors during cleanup.
    } finally {
      solanaClientPromise = null;
    }
  }
}

export function getMetaMaskSolanaDirectProvider() {
  if (!activeWallet?.accounts?.length) return null;

  const wallet = activeWallet;
  const account = wallet.accounts[0];

  return {
    publicKey: account.publicKey,
    signTransaction: async (transaction) => {
      const serialized = transaction.serialize({
        verifySignatures: false,
        requireAllSignatures: false,
      });
      const [result] = await wallet.features[SolanaSignTransaction].signTransaction({
        account,
        chain: SOLANA_MAINNET_CHAIN,
        transaction: serialized,
      });
      return result.signedTransaction;
    },
    signAndSendTransaction: async (transaction) => {
      const serialized = transaction.serialize({
        verifySignatures: false,
        requireAllSignatures: false,
      });
      const [result] = await wallet.features[
        SolanaSignAndSendTransaction
      ].signAndSendTransaction({
        account,
        chain: SOLANA_MAINNET_CHAIN,
        transaction: serialized,
      });
      return result.signature;
    },
  };
}
